import React from "react";
import { useQuery } from "@apollo/client/react";
import Alert from "@mui/material/Alert";
import Link from "@mui/material/Link";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import ConfirmDeleteModal from "../images/ConfirmDeleteModal";
import { ASSETS_REMOVAL_CONFLICTS_QUERY } from "../../constants/graphql/queries";

function CompletedOrderConflicts({ conflicts }) {
  return (
    <Alert severity="error" sx={{ mb: 3, "& .MuiAlert-message": { width: "100%" } }}>
      <Typography variant="body2" gutterBottom>
        These items are on completed orders and cannot be deleted. Deleting them would remove
        them from the order history and break past billing data.
      </Typography>
      <Typography variant="body2" gutterBottom>
        To make inventory match the warehouse instead:
      </Typography>
      <Typography component="ol" variant="body2" sx={{ mt: 0, mb: 1.5, pl: 2.5 }}>
        <li>If the items were received with the wrong quantity, correct the receiving report.</li>
        <li>
          Otherwise, update the item's quantity to match a physical count. The change shows as a
          Location Adjustment in the item's history.
        </li>
      </Typography>

      <Typography variant="body2" fontWeight={600}>
        {conflicts.length} {conflicts.length === 1 ? "item" : "items"} on completed orders
      </Typography>
      <TableContainer sx={{ maxHeight: 240, mt: 0.5 }}>
        <Table
          stickyHeader
          size="small"
          sx={{ "& td, & th": { px: 1, borderColor: "error.light" }, "& th": { bgcolor: "#fdeded" } }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Item ID</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Completed Order</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {conflicts.map(({ itemId, orderId, orderNumber }) => (
              <TableRow key={`${itemId}-${orderId}`}>
                <TableCell>{itemId}</TableCell>
                <TableCell>
                  <Link
                    href={`/inventory/all_orders?q=${encodeURIComponent(orderNumber)}`}
                    target="_blank"
                    rel="noopener"
                  >
                    #{orderNumber}
                  </Link>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Alert>
  );
}

// Checks the selection before the user confirms, so a delete the server will
// refuse (items on completed orders) is explained in the dialog itself.
export default function ConfirmRemoveAssetsModal({ selectedIds, ...props }) {
  const { data, loading } = useQuery(ASSETS_REMOVAL_CONFLICTS_QUERY, {
    variables: { ids: selectedIds },
    fetchPolicy: "network-only",
    skip: !selectedIds.length,
  });
  const conflicts = data?.assetsRemovalConflicts || [];

  return (
    <ConfirmDeleteModal
      {...props}
      checking={loading}
      blockedContent={conflicts.length > 0 && <CompletedOrderConflicts conflicts={conflicts} />}
    />
  );
}
